import { useEffect, useRef, useState } from 'react';
import { Crosshair, Loader2, Search, X } from 'lucide-react';

interface MapCoordinatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (coords: { latitude: number; longitude: number }) => void;
  initialLocation?: { latitude: number; longitude: number };
}

type SearchResult = {
  display_name: string;
  lat: string;
  lon: string;
  kind?: string;
};

export function MapCoordinatePicker({
  isOpen,
  onClose,
  onConfirm,
  initialLocation,
}: MapCoordinatePickerProps) {
  const initialLatitude =
    typeof initialLocation?.latitude === 'number' && Number.isFinite(initialLocation.latitude)
      ? initialLocation.latitude
      : null;
  const initialLongitude =
    typeof initialLocation?.longitude === 'number' && Number.isFinite(initialLocation.longitude)
      ? initialLocation.longitude
      : null;

  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [selectedCoords, setSelectedCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(
    initialLatitude !== null && initialLongitude !== null
      ? { latitude: initialLatitude, longitude: initialLongitude }
      : null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const normalizeQuery = (value: string) =>
    String(value || '')
      .replace(/,+/g, ', ')
      .replace(/\s+/g, ' ')
      .trim();

  const queryTokens = (value: string) =>
    normalizeQuery(value)
      .toLowerCase()
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 2);

  const dedupeResults = (rows: SearchResult[]) => {
    const seen = new Set<string>();
    const result: SearchResult[] = [];

    for (const row of rows) {
      const key = `${Number(row.lat).toFixed(5)}|${Number(row.lon).toFixed(5)}|${String(
        row.display_name || ''
      ).toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(row);
    }

    return result;
  };

  const mapNominatimRows = (rows: any[]): SearchResult[] =>
    rows
      .map((item) => ({
        display_name: String(item?.display_name || '').trim(),
        lat: String(item?.lat || ''),
        lon: String(item?.lon || ''),
        kind: String(item?.type || item?.class || '').trim(),
      }))
      .filter((item) => item.display_name && item.lat && item.lon);

  const scoreRow = (row: SearchResult, tokens: string[]) => {
    const label = String(row.display_name || '').toLowerCase();
    const kind = String(row.kind || '').toLowerCase();

    const tokenMatches = tokens.reduce((acc, token) => (label.includes(token) ? acc + 1 : acc), 0);
    const isSettlement =
      ['city', 'town', 'village', 'hamlet', 'suburb', 'locality', 'administrative'].includes(kind) ||
      /(village|taluka|tehsil|district|city|town|locality|state|india)\b/.test(label);
    const isPoiNoise =
      /(university|college|school|refinery|power|hospital|mall|company|ltd|pvt|station|airport)\b/.test(
        label
      );

    return tokenMatches + (isSettlement ? 3 : 0) - (isPoiNoise ? 2 : 0);
  };

  const rankAndFilterResults = (rows: SearchResult[], rawQuery: string) => {
    const tokens = queryTokens(rawQuery);
    if (!tokens.length) return rows.slice(0, 8);

    const firstToken = tokens[0];
    const withScore = rows.map((row) => ({ row, score: scoreRow(row, tokens) }));

    const firstTokenMatched = withScore.filter((entry) =>
      String(entry.row.display_name || '').toLowerCase().includes(firstToken)
    );

    const basePool = firstTokenMatched.length > 0 ? firstTokenMatched : withScore;
    const minScore = tokens.length >= 3 ? 3 : 2;

    const filtered = basePool
      .filter((entry) => entry.score >= minScore)
      .sort((a, b) => b.score - a.score);

    const source = filtered.length > 0 ? filtered : basePool.sort((a, b) => b.score - a.score);
    return source.map((entry) => entry.row).slice(0, 8);
  };

  const runNominatimSearch = async (query: string): Promise<SearchResult[]> => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&limit=8&addressdetails=1&countrycodes=in`
    );
    if (!response.ok) return [];
    const parsed = await response.json();
    return Array.isArray(parsed) ? mapNominatimRows(parsed) : [];
  };

  useEffect(() => {
    if (!isOpen) return;
    if (initialLatitude !== null && initialLongitude !== null) {
      setSelectedCoords({ latitude: initialLatitude, longitude: initialLongitude });
    }
  }, [isOpen, initialLatitude, initialLongitude]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;
    if (mapInstanceRef.current) return;
    let disposed = false;

    const loadLeaflet = async () => {
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      if (!(window as any).L) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Leaflet'));
          document.body.appendChild(script);
        });
      }

      if (disposed || !mapRef.current) return;

      const L = (window as any).L;
      const center =
        initialLatitude !== null && initialLongitude !== null
          ? [initialLatitude, initialLongitude]
          : [20.5937, 78.9629];

      const map = L.map(mapRef.current, { attributionControl: false }).setView(center, 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);

      const marker = L.marker(center, { draggable: true }).addTo(map);
      mapInstanceRef.current = map;
      markerRef.current = marker;

      setSelectedCoords({
        latitude: Number(center[0]),
        longitude: Number(center[1]),
      });

      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setSelectedCoords({
          latitude: Number(pos.lat),
          longitude: Number(pos.lng),
        });
      });

      map.on('click', (event: any) => {
        const { lat, lng } = event.latlng;
        marker.setLatLng([lat, lng]);
        setSelectedCoords({
          latitude: Number(lat),
          longitude: Number(lng),
        });
      });
    };

    void loadLeaflet();

    return () => {
      disposed = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markerRef.current = null;
    };
  }, [isOpen, initialLatitude, initialLongitude]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation || !mapInstanceRef.current || !markerRef.current) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        mapInstanceRef.current.setView([latitude, longitude], 16);
        markerRef.current.setLatLng([latitude, longitude]);
        setSelectedCoords({ latitude, longitude });
      },
      () => {
        // no-op
      }
    );
  };

  const handleSearchLocation = async () => {
    const query = normalizeQuery(searchQuery);
    if (!query || query.length < 3) {
      setSearchResults([]);
      setSearchMessage('Type at least 3 characters.');
      return;
    }

    setSearching(true);
    setSearchMessage('');

    try {
      const tokens = queryTokens(query);
      const firstToken = tokens[0] || query;

      const queryVariants = Array.from(
        new Set([
          query,
          `${firstToken}, ${query}`,
          `${firstToken} village, ${query}`,
          `${firstToken} town, ${query}`,
          `${firstToken} city, ${query}`,
          `${firstToken} taluka, ${query}`,
        ])
      );

      const nominatimGroups = await Promise.all(queryVariants.map((variant) => runNominatimSearch(variant)));
      const nominatimRows = dedupeResults(nominatimGroups.flat());

      if (nominatimRows.length > 0) {
        const ranked = rankAndFilterResults(nominatimRows, query);
        setSearchResults(ranked);
        setSearchMessage(ranked.length ? '' : 'No exact match found. Add city/state/pincode.');
        return;
      }

      const photonResponse = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=8`
      );
      if (!photonResponse.ok) {
        throw new Error('Search failed');
      }

      const photonData = await photonResponse.json();
      const features = Array.isArray(photonData?.features) ? photonData.features : [];
      const photonRows = dedupeResults(
        features
          .map((feature: any) => {
            const coords = feature?.geometry?.coordinates;
            const lon = Array.isArray(coords) ? coords[0] : undefined;
            const lat = Array.isArray(coords) ? coords[1] : undefined;
            if (typeof lat !== 'number' || typeof lon !== 'number') return null;

            const properties = feature?.properties || {};
            const parts = [
              properties?.name,
              properties?.housenumber,
              properties?.street,
              properties?.suburb,
              properties?.district,
              properties?.city,
              properties?.state,
              properties?.country,
              properties?.postcode,
            ]
              .map((part) => String(part || '').trim())
              .filter(Boolean);

            return {
              display_name: parts.length ? Array.from(new Set(parts)).join(', ') : `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
              lat: String(lat),
              lon: String(lon),
              kind: String(properties?.type || ''),
            } as SearchResult;
          })
          .filter(Boolean) as SearchResult[]
      );

      const ranked = rankAndFilterResults(photonRows, query);
      setSearchResults(ranked);
      setSearchMessage(ranked.length ? '' : 'No results found. Try adding district or pincode.');
    } catch {
      setSearchResults([]);
      setSearchMessage('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSearchResult = (result: SearchResult) => {
    const lat = Number(result.lat);
    const lng = Number(result.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setView([lat, lng], 16);
      markerRef.current.setLatLng([lat, lng]);
    }
    setSelectedCoords({ latitude: lat, longitude: lng });
    setSearchQuery(result.display_name);
    setSearchResults([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Pick Restaurant Location</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="relative z-[2500]">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(normalizeQuery(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleSearchLocation();
                  }
                }}
                placeholder="Search area, landmark, city..."
                className="w-full pl-9 pr-28 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => void handleSearchLocation()}
                disabled={searching}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl z-[2600]">
                {searchResults.map((item, index) => (
                  <button
                    key={`${item.lat}-${item.lon}-${index}`}
                    type="button"
                    onClick={() => handleSelectSearchResult(item)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    {item.display_name}
                  </button>
                ))}
              </div>
            )}
            {searchMessage && searchResults.length === 0 && (
              <p className="mt-2 text-xs text-gray-600">{searchMessage}</p>
            )}
          </div>

          <div ref={mapRef} className="w-full h-[380px] rounded-xl border border-gray-200" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              readOnly
              value={selectedCoords ? selectedCoords.latitude.toFixed(7) : ''}
              placeholder="Latitude"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
            <input
              type="text"
              readOnly
              value={selectedCoords ? selectedCoords.longitude.toFixed(7) : ''}
              placeholder="Longitude"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="px-4 py-2 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center gap-2"
            >
              <Crosshair className="w-4 h-4" />
              Use Current Location
            </button>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => selectedCoords && onConfirm(selectedCoords)}
                disabled={!selectedCoords}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                Confirm Location
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
